import React from "react"

import { format } from "date-fns"
import { enUS, sv } from "date-fns/locale"
import { Form, Formik } from "formik"
import { graphql, useStaticQuery } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { navigate, useIntl } from "gatsby-plugin-intl"
import { padding } from "polished"
import { BallTriangle, Circles } from "react-loader-spinner"
import { useAuth, useDatabaseListData, useDatabaseObjectData } from "reactfire"

import { registerInvoiceOrganization } from "../api"
import {
  Alert,
  bp,
  Button,
  H2,
  H3,
  P,
  shevy,
  SplitImage,
  theme,
  toEm,
  Wrapper,
} from "../components"
import { UnlabeledFormikField } from "../components/form"
import { InvoiceForm } from "../components/invoice"
import NoSsr from "../components/NoSsr"
import SEO from "../components/Seo"
import { groupsDb } from "../firebase"
import { Heading } from "../logged-in/components"
import { useTranslations } from "../utils/intl"

const FormatTimestamp: React.FC<{ timestamp: number }> = ({ timestamp }) => {
  const intl = useIntl()
  return format(timestamp, "d MMMM yyyy", {
    locale: intl.locale == "sv" ? sv : enUS,
  }) as any
}

interface RequestForm {
  organizationName: string
  contactEmail: string
}

type Shared = {
  values?: RequestForm
  contactEmail?: string
}
type State = Shared &
  (
    | { page: "first" }
    | { page: "payment" }
    | { page: "invoice" }
    | { page: "complete" }
  )
type Action =
  | { type: "NEXT" }
  | { type: "VALUES_SET"; values: RequestForm }
  | { type: "BACK" }
  | { type: "COMPLETE"; contactEmail: string }

const requestReducer: React.Reducer<State, Action> = (state, action) => {
  switch (action.type) {
    case "NEXT": {
      switch (state.page) {
        case "first":
          return {
            ...state,
            page: "payment",
          }
        case "payment":
          return {
            ...state,
            page: "invoice",
          }
        default:
          return state
      }
    }
    case "VALUES_SET":
      return {
        ...state,
        values: action.values,
      }
    case "BACK":
      switch (state.page) {
        case "first":
          return state
        case "payment":
          return {
            ...state,
            page: "first",
          }
        case "invoice":
          return {
            ...state,
            page: "payment",
          }
        default:
          return state
      }
    case "COMPLETE":
      return {
        ...state,
        page: "complete",
        contactEmail: action.contactEmail,
      }
  }
}

const BackButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const { it } = useTranslations()

  return (
    <div
      onClick={() => {
        onClick()
      }}
      css={{
        marginBottom: "1em",
        fontSize: "0.8em",
        color: theme.colors.darkGrey,
        cursor: "pointer",
        ":hover": {
          color: theme.colors.black,
        },
      }}
    >
      ← {it(["Back", "Tillbaka"])}
    </div>
  )
}

const SmallH1 = H2.withComponent("h1")

const Complete: React.FC<{ contactEmail?: string }> = ({ contactEmail }) => {
  const { it } = useTranslations()

  const sessionId = localStorage.getItem(SESSION_PENDING_KEY) ?? "no_key"
  const session = useDatabaseObjectData<string | Record<string, unknown>>(
    groupsDb.ref(`/payment-sessions/${sessionId}`)
  ).data

  const email =
    contactEmail ?? new URLSearchParams(location.search).get("email")

  if (typeof session !== "string") {
    return (
      <div css={{ textAlign: "center" }}>
        <SEO title={it([`Payment processing`, `Betalning bearbetas`])} />
        <SmallH1>{it(["Payment processing", "Betalning bearbetas"])}</SmallH1>
        <P>{it(["Almost done!", "Alldeles strax klart!"])}</P>
        <Circles color={theme.colors.green} />
      </div>
    )
  } else if (session === "complete") {
    return (
      <>
        <SEO title={it([`License created`, `Licensen är skapad`])} />
        <SmallH1>{it(["License created", "Licensen är skapad"])}</SmallH1>
        {it([
          <>
            <P>Check your email ({email}) for instructions.</P>
            <H3>Can't find the email?</H3>
            <P>
              Double check your spam folder for an email from{" "}
              <strong>runbit.se</strong>, or send an email to{" "}
              <a href="mailto:anders@runbit.se">anders@runbit.se</a> for help.
            </P>
          </>,
          <>
            <P>Kolla mailen ni angav ({email}) för vidare instruktioner.</P>
            <H3>Fick du inget mail?</H3>
            <P>
              Kolla även i skräp-korgen efter ett mail från{" "}
              <strong>runbit.se</strong> eller maila{" "}
              <a href="mailto:anders@runbit.se">anders@runbit.se</a> ifall något
              gick fel.
            </P>
          </>,
        ])}
      </>
    )
  } else {
    return (
      <Alert>
        {it([
          <>
            <strong>An error occurred</strong> <br />
            <br />
            We're looking at what went wrong and will try to fix it as soon as
            possible possible!
          </>,
          <>
            <strong>Ett fel inträffade</strong> <br />
            <br />
            Vi tittar på vad som gick fel och försöker åtgärda det så snabbt som
            möjligt!
          </>,
        ])}
      </Alert>
    )
  }
}

const LicenseRequestContent: React.FC = ({ ...props }) => {
  const { it } = useTranslations()

  const showComplete = /complete=/.test(location.href) ?? false
  const [state, dispatch] = React.useReducer(requestReducer, {
    page: showComplete ? "complete" : "first",
  })

  const heights: Record<State["page"], string> = {
    first: "880",
    invoice: "1150",
    payment: "800",
    complete: "400",
  }

  const wrapperCss = {
    margin: "0 auto",
    maxWidth: toEm(500),
    padding: toEm(24),

    [bp]: {
      padding: toEm(32),
      marginTop: `max(0px, calc(100vh/2 - ${heights[state.page]}px / 2))`,
    },
  }

  return state.page == "first" ? (
    <div css={wrapperCss} {...props}>
      <Heading title={it(["Create a license", "Skapa en licens"])} />

      <RequestForm
        css={{ marginTop: toEm(40) }}
        initialValues={state.values}
        onSubmit={values => {
          dispatch({ type: "VALUES_SET", values })
          dispatch({ type: "NEXT" })
        }}
      />
    </div>
  ) : state.page == "payment" ? (
    <div css={wrapperCss} {...props}>
      <BackButton
        onClick={() => {
          dispatch({ type: "BACK" })
        }}
      />
      {state.values && (
        <PaymentForm
          values={state.values}
          onInvoice={() => {
            dispatch({ type: "NEXT" })
          }}
        />
      )}
    </div>
  ) : state.page == "invoice" ? (
    <div
      css={{
        margin: "0 auto",
        maxWidth: toEm(800),
        padding: toEm(24),

        [bp]: {
          padding: toEm(32),
        },
      }}
    >
      <BackButton
        onClick={() => {
          dispatch({ type: "BACK" })
        }}
      />
      <OrganizationInvoiceForm
        prefill={
          state.values
            ? {
                org_name: state.values.organizationName,
                invoice_email: state.values.contactEmail,
              }
            : {}
        }
        onSubmit={async formValues => {
          try {
            if (!state.values) throw new Error("Missing state.values")
            const _response = await registerInvoiceOrganization(
              state.values.organizationName,
              state.values.contactEmail,
              formValues
            )
            dispatch({
              type: "COMPLETE",
              contactEmail: state.values.contactEmail,
            })
            localStorage.setItem(SESSION_PENDING_KEY, "no_key")
            navigate(
              `/license-request?complete=true&email=${encodeURIComponent(
                state.values.contactEmail
              )}`
            )
          } catch (e) {
            console.error(e)
            throw new Error(
              it(["An unknown error occurred", "Ett okänt fel inträffade"])
            )
          }
        }}
      />
    </div>
  ) : state.page === "complete" ? (
    <div css={wrapperCss} {...props}>
      <Complete />
    </div>
  ) : (
    <div>Missing page</div>
  )
}

const OrganizationInvoiceForm: React.FC<{
  prefill: Record<string, string>
  onSubmit: (values: Record<string, string>) => Promise<void>
}> = ({ onSubmit, prefill }) => {
  const [error, setError] = React.useState(null as null | string)
  const [loading, setLoading] = React.useState(false)

  return (
    <InvoiceForm
      prefill={prefill}
      onSubmit={values => {
        setLoading(true)
        onSubmit(values).catch(e => {
          setLoading(false)
          setError(e.message)
        })
      }}
      error={error}
      loading={loading}
    />
  )
}

const RequestForm: React.FC<{
  onSubmit: (values: RequestForm) => void
  initialValues?: RequestForm
}> = ({ onSubmit, initialValues, ...props }) => {
  const { it } = useTranslations()
  const email = useAuth().currentUser?.email

  return (
    <div {...props}>
      <Formik<RequestForm>
        initialValues={
          initialValues ?? { organizationName: "", contactEmail: email ?? "" }
        }
        onSubmit={values => {
          onSubmit(values)
        }}
      >
        {() => (
          <Form>
            <ol
              css={{
                margin: 0,
                paddingLeft: 0,
                counterReset: "list1",
                "li:before": {
                  position: "absolute",
                  counterIncrement: "list1",
                  content: 'counter(list1) ". "',
                  left: 0,
                },
                li: {
                  position: "relative",
                  listStyle: "none",
                  paddingLeft: toEm(24),
                  paddingTop: toEm(24),
                  lineHeight: shevy.baseSpacing(1),
                },
                ".formik-field": {
                  paddingTop: toEm(12),
                  paddingBottom: toEm(24),
                },
                button: {
                  marginTop: toEm(12),
                  marginBottom: toEm(24),
                },
              }}
              {...props}
            >
              <li>
                {it(["Enter the name of the school", "Ange skolans namn"])}
              </li>
              <UnlabeledFormikField
                className="formik-field"
                label={it(["School name", "Skolans namn"])}
                name={"organizationName"}
                placeholder={it(["School name", "Holmskolan"])}
                required
              />
              <li>
                {it([
                  "Enter e-mail where instructions with invitation link will be sent",
                  "Ange mail för instruktioner med inbjudningslänk",
                ])}
              </li>
              <UnlabeledFormikField
                className="formik-field"
                label={it(["Email instructions", "Email för instruktioner"])}
                name={"contactEmail"}
                placeholder={it(["example@gmail.com", "exempel@gmail.com"])}
                type="email"
                required
              />
              <li>{it(["Pay by invoice", "Betala med faktura"])}</li>
              <Button
                variant="filled"
                color={theme.colors.green}
                css={{ color: theme.colors.white, alignSelf: "flex-end" }}
              >
                {it(["Payment", "Betala"])}
              </Button>
              <li>
                {it([
                  "Check you e-mail for invite link",
                  "Kolla i mailen efter inbjudningslänk",
                ])}
              </li>
              <li>
                {it([
                  "Share the invite link with students",
                  "Dela länken till alla elever som ska ha appen kostnadsfritt",
                ])}
              </li>
              <li>{it(["Done!", "Klart!"])}</li>
            </ol>
          </Form>
        )}
      </Formik>
    </div>
  )
}

interface PaymentAlternative {
  title: string
  description: string
  length: number
  price: string
  productId: string
}

const SESSION_PENDING_KEY = "PENDING_SESSION_ORG"

const PaymentForm: React.FC<{
  values: RequestForm
  onInvoice: () => void
}> = ({ values: { organizationName }, onInvoice }) => {
  const intl = useIntl()
  const { it } = useTranslations()
  const paymentAlternatives = useDatabaseListData<PaymentAlternative>(
    groupsDb.ref(`/payment-alternatives/${intl.locale}`)
  ).data

  const prices = paymentAlternatives.map(p => ({
    price: p.price,
    productId: p.productId,
    title: p.title,
    length: p.length,
    description: p.description,
  }))
  const productId = prices[prices.length - 1].productId
  const currentProduct = prices.find(p => p.productId == productId)
  if (!currentProduct) {
    throw new Error("Selected product not in available products")
  }

  return (
    <div>
      <SEO
        title={it([
          `Payment for ${organizationName}`,
          `Betala för ${organizationName}`,
        ])}
      />
      <Heading
        css={{ marginBottom: shevy.baseSpacing(2) }}
        title={organizationName}
        // subtitle={it([<>en</>, <>sv</>])}
      />
      <h2
        css={{
          fontSize: toEm(20 * 1.25),
          marginBottom: shevy.baseSpacing(0.2),
        }}
      >
        {it(["Full version", "Fullständig version"])}
      </h2>
      <P>
        {it([
          `Everyone that registers with your link receive the full version of Runbit completely free of charge.`,
          `Alla som registrerar sig med er länk får fullständig version av Runbit helt kostnadsfritt.`,
        ])}
      </P>
      <h2
        css={{
          fontSize: toEm(20 * 1.25),
          marginBottom: shevy.baseSpacing(0.2),
        }}
      >
        {it(["Refunds", "Ångra köp"])}
      </h2>
      <P>
        {it([
          `Did you change your mind, or perhaps it didn't work as you imagined? Contact us withing 30 days to get your money back.`,
          `Har du ångrat dig eller fungerar det inte som ni tänkt er? Kontakta oss inom 30 dagar så får ni pengarna tillbaka.`,
        ])}
      </P>

      <div
        css={{
          marginTop: shevy.baseSpacing(2),
          backgroundColor: theme.colors.blue,
          borderRadius: toEm(12),
          color: "white",
          ...padding(toEm(48), toEm(48)),
        }}
      >
        <p
          css={{
            textTransform: "uppercase",
            fontSize: "0.9em",
            fontWeight: 700,
            marginBottom: shevy.baseSpacing(0.5),
          }}
        >
          {it(["One time cost", "Engångskostnad"])}
        </p>
        <p css={{ fontSize: "3em", fontWeight: 700, marginBottom: 0 }}>
          {prices.find(p => p.productId == productId)?.price}
        </p>
        <p
          css={{
            fontSize: "0.9em",
            marginBottom: shevy.baseSpacing(2),
          }}
        >
          {it(["ex. VAT", "ex. moms"])}
        </p>
        <p
          css={{
            fontSize: "1.25em",
            fontWeight: 600,
            marginBottom: shevy.baseSpacing(0.5),
          }}
        >
          {it([
            <>
              Today -{" "}
              <FormatTimestamp timestamp={Date.now() + currentProduct.length} />
            </>,
            <>
              Idag -{" "}
              <FormatTimestamp timestamp={Date.now() + currentProduct.length} />
            </>,
          ])}
        </p>
        <p
          css={{
            marginBottom: shevy.baseSpacing(2),
          }}
        >
          {it([
            <>
              Everyone that registers with "{organizationName}" will receive the
              full version of the app until{" "}
              <FormatTimestamp timestamp={Date.now() + currentProduct.length} />
            </>,
            <>
              Alla som registrerar sig med "{organizationName}" kommer få den
              fullständiga versionen av appen till{" "}
              <FormatTimestamp timestamp={Date.now() + currentProduct.length} />
            </>,
          ])}
        </p>
        {currentProduct.length >= 31536000000 && (
          <Button
            css={{ width: "100%", marginTop: shevy.baseSpacing() }}
            color={theme.colors.white}
            variant="filled"
            onClick={e => {
              e.preventDefault()
              onInvoice()
            }}
          >
            {it(["Pay with invoice", "Betala med faktura"])}
          </Button>
        )}
      </div>
    </div>
  )
}

const LicenseRequest: React.FC = () => {
  const {
    file: { childImageSharp: image },
  } = useStaticQuery(graphql`
    query {
      file(relativePath: { eq: "phone_with_stuff.png" }) {
        childImageSharp {
          # Specify the image processing specifications right in the query.
          # Makes it trivial to update as your page's design changes.
          gatsbyImageData(
            sizes: "max(50vw, 100vh)"
            quality: 80
            breakpoints: [500, 960, 1024, 1920, 2100]
            placeholder: TRACED_SVG
            tracedSVGOptions: { background: "#ffcb66", color: "#FFBB34" }
          )
        }
      }
    }
  `)

  return (
    <Wrapper>
      <SplitImage
        background={theme.colors.orange}
        // css={{ : theme.colors.white }}
        image={
          <GatsbyImage alt="Screenshot of Runbit" image={getImage(image)!} />
        }
      >
        <NoSsr>
          <React.Suspense
            fallback={
              <BallTriangle css={{ textAlign: "center" }} color="#ED5351" />
            }
          >
            <LicenseRequestContent />
          </React.Suspense>
        </NoSsr>
      </SplitImage>
    </Wrapper>
  )
}

export default LicenseRequest
